import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  User, Clock, Wallet, Calendar, Briefcase, Package, 
  ArrowLeft, Loader2, CheckCircle, Sun, CloudSun, Check, X, FileText, Download, Warehouse, RotateCcw
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token') || localStorage.getItem('fortis_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MyInfo = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('mesai');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [dailyAttendance, setDailyAttendance] = useState({ sabah: null, oglen: null });
  const [monthlyAttendance, setMonthlyAttendance] = useState(null);
  const [advancesData, setAdvancesData] = useState(null);
  const [deductionsData, setDeductionsData] = useState(null);
  const [leavesData, setLeavesData] = useState(null);
  const [overtimes, setOvertimes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetsArchive, setAssetsArchive] = useState([]);
  const [zimmetTab, setZimmetTab] = useState('aktif'); // 'aktif' | 'arsiv'
  const [depoTab, setDepoTab] = useState('guncel'); // 'guncel' | 'arsiv'
  const [depodanAlinanlar, setDepodanAlinanlar] = useState({ items: [], total_count: 0 });
  const [hrWorks, setHrWorks] = useState([]);
  const [hrWorkTab, setHrWorkTab] = useState(null); // seçili belge adı (title)
  
  // Zimmet kabul/red state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingAsset, setProcessingAsset] = useState(null);
  
  const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
                      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  
  // Filter states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [mesaiTab, setMesaiTab] = useState('daily');
  const [avansTab, setAvansTab] = useState('cekilen');
  
  // Yıl listesi
  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear, currentYear + 1];

  const months = [
    { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' }, { value: 3, label: 'Mart' },
    { value: 4, label: 'Nisan' }, { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
    { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' }, { value: 9, label: 'Eylül' },
    { value: 10, label: 'Ekim' }, { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' }
  ];

  const menuItems = [
    { id: 'mesai', label: 'Mesai Durumum', icon: Clock },
    { id: 'avans', label: 'Avanslarım', icon: Wallet },
    { id: 'izin', label: 'İzinlerim', icon: Calendar },
    { id: 'fazlamesai', label: 'Fazla Mesailerim', icon: Briefcase },
    { id: 'zimmet', label: 'Zimmetimdekiler', icon: Package },
    { id: 'depodan', label: 'Depodan Alınanlar', icon: Warehouse },
    { id: 'ikisler', label: 'İK İşler', icon: FileText },
  ];

  useEffect(() => {
    if (activeSection === 'mesai') {
      if (mesaiTab === 'daily') loadDailyAttendance();
      else loadMonthlyAttendance();
    }
    else if (activeSection === 'avans') loadAdvances();
    else if (activeSection === 'izin') loadLeaves();
    else if (activeSection === 'fazlamesai') loadOvertimes();
    else if (activeSection === 'zimmet') loadAssets();
    else if (activeSection === 'depodan') { loadDepodanAlinanlar(); loadAssets(); }
    else if (activeSection === 'ikisler') loadHrWorks();
  }, [activeSection, selectedMonth, selectedYear, mesaiTab]);

  const loadDailyAttendance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/daily-attendance`, { headers: getAuthHeaders() });
      setDailyAttendance({
        sabah: res.data.sabah,
        oglen: res.data.oglen,
        sabah_time: res.data.sabah_time,
        oglen_time: res.data.oglen_time,
        date: res.data.date
      });
    } catch (error) {
      console.error('Günlük mesai yüklenirken hata:', error);
      setDailyAttendance({ sabah: null, oglen: null });
    }
    setLoading(false);
  };

  const loadMonthlyAttendance = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/monthly-attendance`, {
        params: { month: selectedMonth, year: selectedYear },
        headers: getAuthHeaders()
      });
      setMonthlyAttendance(res.data);
    } catch (error) {
      console.error('Aylık mesai yüklenirken hata:', error);
      setMonthlyAttendance(null);
    }
    setLoading(false);
  };

  const submitWorkStart = async (period) => {
    try {
      await axios.post(`${API}/my/daily-attendance`, 
        { period },
        { headers: getAuthHeaders() }
      );
      toast.success(`${period === 'sabah' ? 'Sabah' : 'Öğlen'} iş başlangıcı kaydedildi`);
      loadDailyAttendance();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Kayıt yapılamadı';
      toast.error(msg);
    }
  };

  const loadAdvances = async () => {
    setLoading(true);
    try {
      const [advRes, dedRes] = await Promise.all([
        axios.get(`${API}/my/advances`, { 
          params: { year: selectedYear },
          headers: getAuthHeaders() 
        }).catch(() => ({ data: null })),
        axios.get(`${API}/my/advance-deductions`, { 
          params: { year: selectedYear },
          headers: getAuthHeaders() 
        }).catch(() => ({ data: null }))
      ]);
      setAdvancesData(advRes.data);
      setDeductionsData(dedRes.data);
    } catch (error) {
      console.error('Avans verileri yüklenirken hata:', error);
    }
    setLoading(false);
  };

  const loadLeaves = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/leaves`, {
        params: { year: selectedYear },
        headers: getAuthHeaders()
      });
      setLeavesData(res.data);
    } catch (error) {
      console.error('İzin verileri yüklenirken hata:', error);
      setLeavesData(null);
    }
    setLoading(false);
  };

  const loadOvertimes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/overtimes`, {
        params: { month: selectedMonth, year: selectedYear },
        headers: getAuthHeaders()
      });
      setOvertimes(res.data);
    } catch (error) {
      console.error('Fazla mesai verileri yüklenirken hata:', error);
      setOvertimes(null);
    }
    setLoading(false);
  };

  const loadAssets = async () => {
    setLoading(true);
    try {
      const [activeRes, archiveRes] = await Promise.all([
        axios.get(`${API}/my/assets`, { headers: getAuthHeaders() }),
        axios.get(`${API}/my/assets/archive`, { headers: getAuthHeaders() })
      ]);
      setAssets(activeRes.data || []);
      setAssetsArchive(archiveRes.data || []);
    } catch (error) {
      console.error('Zimmet verileri yüklenirken hata:', error);
      setAssets([]);
      setAssetsArchive([]);
    }
    setLoading(false);
  };

  const loadDepodanAlinanlar = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/my/depot-alinanlar`, { headers: getAuthHeaders() });
      setDepodanAlinanlar(res.data || { items: [], total_count: 0 });
    } catch (error) {
      console.error('Depodan alınanlar yüklenirken hata:', error);
      setDepodanAlinanlar({ items: [], total_count: 0 });
    }
    setLoading(false);
  };

  const loadHrWorks = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employee/document-requests`, { headers: getAuthHeaders() });
      const data = res.data || [];
      setHrWorks(data);
      // İlk belge adını (title) otomatik seç
      const titles = [...new Set(data.map(d => (d.title || 'Diğer').trim()))];
      setHrWorkTab(prev => (prev && titles.includes(prev)) ? prev : (titles[0] || null));
    } catch (error) {
      console.error('İK işler yüklenirken hata:', error);
      setHrWorks([]);
    }
    setLoading(false);
  };

  const handleAcceptAsset = async (assetId) => {
    setProcessingAsset(assetId);
    try {
      await axios.post(`${API}/my/assets/${assetId}/accept`, {}, { headers: getAuthHeaders() });
      toast.success('Zimmet kabul edildi');
      loadAssets(); // Listeyi yenile
    } catch (error) {
      console.error('Zimmet kabul edilirken hata:', error);
      toast.error('Zimmet kabul edilemedi');
    }
    setProcessingAsset(null);
  };

  const handleRejectAsset = async () => {
    if (!selectedAsset) return;
    
    setProcessingAsset(selectedAsset.id);
    try {
      await axios.post(`${API}/my/assets/${selectedAsset.id}/reject`, { 
        sebep: rejectReason || 'Sebep belirtilmedi' 
      }, { headers: getAuthHeaders() });
      toast.success('Zimmet reddedildi ve depoya geri döndü');
      setShowRejectModal(false);
      setSelectedAsset(null);
      setRejectReason('');
      loadAssets(); // Listeyi yenile
    } catch (error) {
      console.error('Zimmet reddedilirken hata:', error);
      toast.error('Zimmet reddedilemedi');
    }
    setProcessingAsset(null);
  };

  const handleReturnAsset = async (assetId) => {
    if (!window.confirm('Bu zimmeti iade etmek istediğinize emin misiniz? Ürün depoya geri dönecektir.')) return;
    setProcessingAsset(assetId);
    try {
      await axios.post(`${API}/my/assets/${assetId}/return`, {}, { headers: getAuthHeaders() });
      toast.success('Zimmet iade edildi ve depoya geri döndü');
      loadAssets(); // Listeyi yenile
    } catch (error) {
      console.error('Zimmet iade edilirken hata:', error);
      toast.error('Zimmet iade edilemedi');
    }
    setProcessingAsset(null);
  };
  // Depo Stok Çıkışı Onay/Red Fonksiyonları
  const [showDepoRejectModal, setShowDepoRejectModal] = useState(false);
  const [selectedDepoItem, setSelectedDepoItem] = useState(null);
  const [depoRejectReason, setDepoRejectReason] = useState('');
  const [processingDepoItem, setProcessingDepoItem] = useState(null);

  const handleApproveDepoItem = async (cikisId) => {
    setProcessingDepoItem(cikisId);
    try {
      await axios.post(`${API}/my/depot-alinanlar/${cikisId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success('Teslim alma onaylandı, stoktan düşüldü');
      loadDepodanAlinanlar(); // Listeyi yenile
    } catch (error) {
      console.error('Onay hatası:', error);
      const errMsg = error.response?.data?.detail || 'Onaylama işlemi başarısız';
      toast.error(errMsg);
    }
    setProcessingDepoItem(null);
  };

  const handleRejectDepoItem = async () => {
    if (!selectedDepoItem) return;
    
    setProcessingDepoItem(selectedDepoItem.id);
    try {
      await axios.post(`${API}/my/depot-alinanlar/${selectedDepoItem.id}/reject`, { 
        reason: depoRejectReason || 'Sebep belirtilmedi' 
      }, { headers: getAuthHeaders() });
      toast.success('Teslim alma reddedildi');
      setShowDepoRejectModal(false);
      setSelectedDepoItem(null);
      setDepoRejectReason('');
      loadDepodanAlinanlar(); // Listeyi yenile
    } catch (error) {
      console.error('Red hatası:', error);
      const errMsg = error.response?.data?.detail || 'Reddetme işlemi başarısız';
      toast.error(errMsg);
    }
    setProcessingDepoItem(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return 'bg-green-100 text-green-700';
      case 'half_day': return 'bg-lime-100 text-lime-700';
      case 'absent': return 'bg-red-100 text-red-700';
      case 'paid_leave': return 'bg-blue-100 text-blue-700';
      case 'unpaid_leave': return 'bg-gray-100 text-gray-700';
      case 'sick': return 'bg-purple-100 text-purple-700';
      case 'annual': return 'bg-yellow-100 text-yellow-700';
      case 'holiday': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-50';
    }
  };

  const getStatusShort = (status) => {
    switch (status) {
      case 'present': return 'G';
      case 'half_day': return 'YG';
      case 'absent': return 'X';
      case 'paid_leave': return 'Üİ';
      case 'unpaid_leave': return 'Üsİ';
      case 'sick': return 'R';
      case 'annual': return 'Yİ';
      case 'holiday': return 'RT';
      default: return '-';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount || 0);
  };

  const getTodayFormatted = () => {
    const today = new Date();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return today.toLocaleDateString('tr-TR', options);
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month, 0).getDate();
  };

  // Zimmet ayrımı: kalıcı (zimmette) -> Zimmetimdekiler, geçici (kullanicida) -> Depodan Alınanlar
  const permanentAssets = assets.filter(a => a.is_permanent);
  const temporaryAssets = assets.filter(a => !a.is_permanent);
  const zimmetArchive = assetsArchive.filter(a => a.zimmet_durumu === 'zimmette');
  const depoZimmetArchive = assetsArchive.filter(a => a.zimmet_durumu === 'kullanicida');

  // İK İşler: belge taleplerini ada (title) göre grupla
  const hrWorkTitles = [...new Set(hrWorks.map(d => (d.title || 'Diğer').trim()))];
  const hrWorkEntries = hrWorks
    .filter(d => (d.title || 'Diğer').trim() === hrWorkTab)
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return (
    <div className="p-6 space-y-6" data-testid="my-info-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user?.name || 'Kullanıcı'}</h1>
            <p className="text-gray-500">Kişisel Bilgilerim</p>
          </div>
        </div>
        
        {/* Month/Year selector - sadece aylık görünümde */}
        {(activeSection !== 'mesai' || mesaiTab !== 'daily') && (
          <div className="flex items-center gap-2">
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map(m => (
                  <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar Menu */}
        <div className="col-span-3">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-2">
                {menuItems.map(item => (
                  <Button
                    key={item.id}
                    variant={activeSection === item.id ? 'default' : 'ghost'}
                    className="w-full justify-start"
                    onClick={() => setActiveSection(item.id)}
                    data-testid={`menu-${item.id}`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="col-span-9">
          {loading ? (
            <Card>
              <CardContent className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mesai Durumum */}
              {activeSection === 'mesai' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Mesai Durumum
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={mesaiTab} onValueChange={setMesaiTab}>
                      <TabsList className="mb-4">
                        <TabsTrigger value="daily">Günlük Mesai</TabsTrigger>
                        <TabsTrigger value="monthly">Aylık Mesai</TabsTrigger>
                      </TabsList>
                      
                      {/* GÜNLÜK MESAİ - Sadece İş Başlangıcı Butonu */}
                      <TabsContent value="daily">
                        <div className="space-y-6">
                          {/* Bugünün Tarihi */}
                          <div className="text-center py-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border">
                            <p className="text-2xl font-bold text-indigo-700">{getTodayFormatted()}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-6">
                            {/* SABAH */}
                            <div className="border rounded-lg p-6 bg-gradient-to-b from-amber-50 to-white">
                              <div className="flex items-center justify-center gap-2 mb-6">
                                <Sun className="h-8 w-8 text-amber-500" />
                                <h3 className="text-2xl font-bold text-amber-700">SABAH</h3>
                              </div>
                              
                              {dailyAttendance.sabah ? (
                                <div className="text-center py-4">
                                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg mb-3">
                                    <CheckCircle className="h-5 w-5" />
                                    <span className="font-semibold">İş Başlangıcı Kaydedildi</span>
                                  </div>
                                  <div className="space-y-1 text-gray-600">
                                    <p className="text-lg font-medium">{dailyAttendance.date}</p>
                                    <p className="text-2xl font-bold text-green-600">{dailyAttendance.sabah_time}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Button
                                    size="lg"
                                    className="bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-lg"
                                    onClick={() => submitWorkStart('sabah')}
                                    data-testid="sabah-start"
                                  >
                                    <Clock className="h-5 w-5 mr-2" />
                                    İş Başlangıcı
                                  </Button>
                                  <p className="text-sm text-gray-500 mt-3">Butona basarak iş başlangıcınızı bildirin</p>
                                </div>
                              )}
                            </div>

                            {/* ÖĞLEN */}
                            <div className="border rounded-lg p-6 bg-gradient-to-b from-orange-50 to-white">
                              <div className="flex items-center justify-center gap-2 mb-6">
                                <CloudSun className="h-8 w-8 text-orange-500" />
                                <h3 className="text-2xl font-bold text-orange-700">ÖĞLEN</h3>
                              </div>
                              
                              {dailyAttendance.oglen ? (
                                <div className="text-center py-4">
                                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg mb-3">
                                    <CheckCircle className="h-5 w-5" />
                                    <span className="font-semibold">İş Başlangıcı Kaydedildi</span>
                                  </div>
                                  <div className="space-y-1 text-gray-600">
                                    <p className="text-lg font-medium">{dailyAttendance.date}</p>
                                    <p className="text-2xl font-bold text-green-600">{dailyAttendance.oglen_time}</p>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Button
                                    size="lg"
                                    className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg"
                                    onClick={() => submitWorkStart('oglen')}
                                    data-testid="oglen-start"
                                  >
                                    <Clock className="h-5 w-5 mr-2" />
                                    İş Başlangıcı
                                  </Button>
                                  <p className="text-sm text-gray-500 mt-3">Butona basarak iş başlangıcınızı bildirin</p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-700">
                              <strong>Bilgi:</strong> Bu butonlar sadece yöneticinize işte olduğunuzu bildirmek içindir. 
                              Resmi mesai kaydınız İK birimi tarafından ayrıca tutulmaktadır.
                            </p>
                          </div>
                        </div>
                      </TabsContent>
                      
                      {/* AYLIK MESAİ - İK PANELİNDEN GELEN VERİ */}
                      <TabsContent value="monthly">
                        {monthlyAttendance ? (
                          <div className="space-y-4">
                            {/* Kullanıcı Bilgisi */}
                            <div className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg">
                              <div>
                                <p className="font-bold text-indigo-800">{monthlyAttendance.name}</p>
                                <p className="text-sm text-indigo-600">
                                  {monthlyAttendance.branch_name} {monthlyAttendance.position_name && `- ${monthlyAttendance.position_name}`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-indigo-700">
                                  {monthNames[selectedMonth - 1]} {selectedYear}
                                </p>
                              </div>
                            </div>

                            {/* Aylık Mesai Tablosu - İK Panelinden Gelen */}
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow className="bg-indigo-100">
                                    {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1).map(day => (
                                      <TableHead key={day} className="text-center font-bold min-w-[35px] p-1">
                                        {day}
                                      </TableHead>
                                    ))}
                                    <TableHead className="bg-green-100 font-bold text-center min-w-[60px]">Geldi</TableHead>
                                    <TableHead className="bg-lime-100 font-bold text-center min-w-[50px]">YG</TableHead>
                                    <TableHead className="bg-red-100 font-bold text-center min-w-[60px]">Gelmedi</TableHead>
                                    <TableHead className="bg-blue-100 font-bold text-center min-w-[50px]">Üİ</TableHead>
                                    <TableHead className="bg-gray-100 font-bold text-center min-w-[50px]">Üsİ</TableHead>
                                    <TableHead className="bg-purple-100 font-bold text-center min-w-[50px]">R</TableHead>
                                    <TableHead className="bg-yellow-100 font-bold text-center min-w-[50px]">Yİ</TableHead>
                                    <TableHead className="bg-orange-100 font-bold text-center min-w-[50px]">RT</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  <TableRow>
                                    {Array.from({ length: getDaysInMonth(selectedYear, selectedMonth) }, (_, i) => i + 1).map(day => {
                                      const status = monthlyAttendance.attendance?.[day];
                                      return (
                                        <TableCell key={day} className="text-center p-1">
                                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold ${getStatusColor(status)}`}>
                                            {getStatusShort(status)}
                                          </span>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell className="text-center font-bold bg-green-50 text-green-700">
                                      {monthlyAttendance.summary?.present || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-lime-50 text-lime-700">
                                      {monthlyAttendance.summary?.half_day || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-red-50 text-red-700">
                                      {monthlyAttendance.summary?.absent || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-blue-50 text-blue-700">
                                      {monthlyAttendance.summary?.paid_leave || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-gray-50 text-gray-700">
                                      {monthlyAttendance.summary?.unpaid_leave || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-purple-50 text-purple-700">
                                      {monthlyAttendance.summary?.sick || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-yellow-50 text-yellow-700">
                                      {monthlyAttendance.summary?.annual || '-'}
                                    </TableCell>
                                    <TableCell className="text-center font-bold bg-orange-50 text-orange-700">
                                      {monthlyAttendance.summary?.holiday || '-'}
                                    </TableCell>
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </div>

                            {/* Lejant */}
                            <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-green-100 text-green-700">G</span>
                                <span className="text-sm">İşe Geldi</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-lime-100 text-lime-700">YG</span>
                                <span className="text-sm">Yarım Gün</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-red-100 text-red-700">X</span>
                                <span className="text-sm">Gelmedi</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-blue-100 text-blue-700">Üİ</span>
                                <span className="text-sm">Ücretli İzinli</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-gray-100 text-gray-700">Üsİ</span>
                                <span className="text-sm">Ücretsiz İzinli</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-purple-100 text-purple-700">R</span>
                                <span className="text-sm">Raporlu</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-yellow-100 text-yellow-700">Yİ</span>
                                <span className="text-sm">Yıllık İzin</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-flex items-center justify-center w-6 h-6 rounded text-xs font-bold bg-orange-100 text-orange-700">RT</span>
                                <span className="text-sm">Resmi Tatil</span>
                              </div>
                            </div>

                            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm text-blue-700">
                                <strong>Bilgi:</strong> Bu tablo İK birimi tarafından tutulan resmi mesai kaydınızı göstermektedir.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Bu ay için mesai kaydı bulunmuyor</p>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}

              {/* Avanslarım */}
              {activeSection === 'avans' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Wallet className="h-5 w-5" />
                      Avanslarım - {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={avansTab} onValueChange={setAvansTab}>
                      <TabsList className="mb-4">
                        <TabsTrigger value="cekilen">Çekilen Avanslar</TabsTrigger>
                        <TabsTrigger value="kesinti">Aylık Avans Kesintileri</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="cekilen">
                        {advancesData ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="font-semibold">Ay</TableHead>
                                  <TableHead className="text-right font-semibold">Tutar</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {monthNames.map((monthName, idx) => {
                                  const amount = advancesData.advances?.[idx + 1] || 0;
                                  return (
                                    <TableRow key={idx} className={amount > 0 ? 'bg-green-50' : ''}>
                                      <TableCell>{monthName}</TableCell>
                                      <TableCell className="text-right">
                                        {amount > 0 ? (
                                          <span className="font-semibold text-green-700">{formatCurrency(amount)}</span>
                                        ) : '-'}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                <TableRow className="bg-gray-100 font-bold">
                                  <TableCell>TOPLAM</TableCell>
                                  <TableCell className="text-right text-green-700">
                                    {formatCurrency(advancesData.total_advances || 0)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Wallet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Çekilen avans verisi yüklenemedi</p>
                          </div>
                        )}
                      </TabsContent>
                      
                      <TabsContent value="kesinti">
                        {deductionsData ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="font-semibold">Ay</TableHead>
                                  <TableHead className="text-right font-semibold">Kesinti</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {monthNames.map((monthName, idx) => {
                                  const amount = deductionsData.deductions?.[idx + 1] || 0;
                                  return (
                                    <TableRow key={idx} className={amount > 0 ? 'bg-red-50' : ''}>
                                      <TableCell>{monthName}</TableCell>
                                      <TableCell className="text-right">
                                        {amount > 0 ? (
                                          <span className="font-semibold text-red-700">{formatCurrency(amount)}</span>
                                        ) : '-'}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                                <TableRow className="bg-gray-100 font-bold">
                                  <TableCell>TOPLAM</TableCell>
                                  <TableCell className="text-right text-red-700">
                                    {formatCurrency(deductionsData.total_deductions || 0)}
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Wallet className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Avans kesintisi verisi yüklenemedi</p>
                          </div>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}

              {/* İzinlerim */}
              {activeSection === 'izin' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Calendar className="h-5 w-5" />
                      İzinlerim - {selectedYear} Yıllık Özet
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {leavesData ? (
                      <div className="space-y-4">
                        {/* İK Panelindeki tablo formatı - Yatay aylar */}
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-cyan-100">
                                <TableHead className="sticky left-0 bg-cyan-100 z-10 font-bold">Çalışan</TableHead>
                                {monthNames.map((month, idx) => (
                                  <TableHead key={idx} className="text-center font-bold min-w-[100px]">
                                    {month}
                                  </TableHead>
                                ))}
                                <TableHead className="text-center font-bold bg-red-100 min-w-[70px]">Gelmedi</TableHead>
                                <TableHead className="text-center font-bold bg-blue-100 min-w-[90px]">Ücretli İzinli</TableHead>
                                <TableHead className="text-center font-bold bg-cyan-100 min-w-[100px]">Ücretsiz İzinli</TableHead>
                                <TableHead className="text-center font-bold bg-purple-100 min-w-[70px]">Raporlu</TableHead>
                                <TableHead className="text-center font-bold bg-yellow-100 min-w-[80px]">Yıllık İzin</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow className="hover:bg-cyan-50">
                                <TableCell className="sticky left-0 bg-white z-10 font-medium border-r">
                                  {leavesData.name || user?.name || 'Kullanıcı'}
                                </TableCell>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => {
                                  const monthData = leavesData.months?.[month] || {};
                                  const hasData = monthData.annual > 0 || monthData.sick > 0 || 
                                                  monthData.paid_leave > 0 || monthData.unpaid_leave > 0 || 
                                                  monthData.absent > 0;
                                  return (
                                    <TableCell key={month} className="text-center p-2">
                                      {hasData ? (
                                        <div className="text-xs space-y-0.5">
                                          {monthData.annual > 0 && (
                                            <div className="text-yellow-600">Y.İzin: {monthData.annual}</div>
                                          )}
                                          {monthData.sick > 0 && (
                                            <div className="text-purple-600">Rapor: {monthData.sick}</div>
                                          )}
                                          {monthData.paid_leave > 0 && (
                                            <div className="text-blue-600">İzin: {monthData.paid_leave}</div>
                                          )}
                                          {monthData.unpaid_leave > 0 && (
                                            <div className="text-cyan-600">Üsİz: {monthData.unpaid_leave}</div>
                                          )}
                                          {monthData.absent > 0 && (
                                            <div className="text-red-600">Gelmedi: {monthData.absent}</div>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-300">-</span>
                                      )}
                                    </TableCell>
                                  );
                                })}
                                <TableCell className="text-center font-bold bg-red-50 text-red-700">
                                  {leavesData.totals?.absent || '-'}
                                </TableCell>
                                <TableCell className="text-center font-bold bg-blue-50 text-blue-700">
                                  {leavesData.totals?.paid_leave || '-'}
                                </TableCell>
                                <TableCell className="text-center font-bold bg-cyan-50 text-cyan-700">
                                  {leavesData.totals?.unpaid_leave || '-'}
                                </TableCell>
                                <TableCell className="text-center font-bold bg-purple-50 text-purple-700">
                                  {leavesData.totals?.sick || '-'}
                                </TableCell>
                                <TableCell className="text-center font-bold bg-yellow-50 text-yellow-700">
                                  {leavesData.totals?.annual || '-'}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </div>

                        {/* Lejant - İK Panelindeki ile aynı */}
                        <div className="flex flex-wrap gap-4 p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                            <span className="text-sm">Y.İzin = Yıllık İzin</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                            <span className="text-sm">Rapor = Raporlu</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                            <span className="text-sm">İzin = Ücretli İzinli</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-cyan-500"></span>
                            <span className="text-sm">Üsİz = Ücretsiz İzinli</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full bg-red-500"></span>
                            <span className="text-sm">Gelmedi = İşe Gelmedi</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>İzin verisi yüklenemedi</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Fazla Mesailerim */}
              {activeSection === 'fazlamesai' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Briefcase className="h-5 w-5" />
                      Fazla Mesailerim - {monthNames[selectedMonth - 1]} {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {overtimes ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <Card className="bg-blue-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-blue-600">
                                {overtimes.summary?.weekday_hours || 0}
                              </p>
                              <p className="text-sm text-gray-600">Hafta İçi (Saat)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-orange-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-orange-600">
                                {overtimes.summary?.holiday_hours || 0}
                              </p>
                              <p className="text-sm text-gray-600">Tatil Günü (Saat)</p>
                            </CardContent>
                          </Card>
                          <Card className="bg-green-50">
                            <CardContent className="p-4 text-center">
                              <p className="text-2xl font-bold text-green-600">
                                {overtimes.summary?.total_hours || 0}
                              </p>
                              <p className="text-sm text-gray-600">Toplam (Saat)</p>
                            </CardContent>
                          </Card>
                        </div>

                        {overtimes.records && overtimes.records.length > 0 ? (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Tarih</TableHead>
                                <TableHead>Gün</TableHead>
                                <TableHead className="text-center">Saat</TableHead>
                                <TableHead className="text-center">Tür</TableHead>
                                <TableHead>Açıklama</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {overtimes.records.map((ot, idx) => (
                                <TableRow key={idx} className={ot.is_holiday ? 'bg-orange-50' : ''}>
                                  <TableCell>{formatDate(ot.date)}</TableCell>
                                  <TableCell>{ot.day || '-'}</TableCell>
                                  <TableCell className="text-center font-semibold">{ot.hours || 0}</TableCell>
                                  <TableCell className="text-center">
                                    {ot.is_holiday ? (
                                      <Badge className="bg-orange-100 text-orange-700">Tatil</Badge>
                                    ) : (
                                      <Badge className="bg-blue-100 text-blue-700">Hafta İçi</Badge>
                                    )}
                                  </TableCell>
                                  <TableCell>{ot.description || '-'}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                            <p>Bu ay için fazla mesai kaydı bulunmuyor</p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Briefcase className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Fazla mesai verisi yüklenemedi</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Zimmetimdekiler */}
              {activeSection === 'zimmet' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Zimmetimdekiler
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Alt sekmeler: Aktif / Arşiv */}
                    <div className="flex gap-2 mb-4 border-b">
                      <button
                        type="button"
                        onClick={() => setZimmetTab('aktif')}
                        data-testid="zimmet-tab-aktif"
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${zimmetTab === 'aktif' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      >
                        Aktif Zimmetler ({permanentAssets.length})
                      </button>
                      <button
                        type="button"
                        onClick={() => setZimmetTab('arsiv')}
                        data-testid="zimmet-tab-arsiv"
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${zimmetTab === 'arsiv' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      >
                        Arşiv ({zimmetArchive.length})
                      </button>
                    </div>

                    {zimmetTab === 'aktif' && (
                    permanentAssets.length > 0 ? (
                      <div className="space-y-4">
                        {permanentAssets.map((asset, idx) => (
                          <div key={asset.id || idx} className="border rounded-lg p-4 space-y-3">
                            {/* Ürün Bilgileri */}
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-lg">{asset.name || asset.asset_name}</h4>
                                <p className="text-sm text-gray-600">
                                  {asset.brand && `${asset.brand} `}
                                  {asset.model && `${asset.model} `}
                                </p>
                              </div>
                              {asset.status === 'pending' ? (
                                <Badge className="bg-yellow-100 text-yellow-700">Onay Bekliyor</Badge>
                              ) : (
                                <div className="flex flex-col items-end gap-1">
                                  <Badge className="bg-green-100 text-green-700">Kabul Edildi</Badge>
                                  {asset.is_permanent && (
                                    <Badge className="bg-indigo-100 text-indigo-700">Kalıcı Zimmet</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Detaylar */}
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">Seri No:</span>
                                <span className="ml-2 font-mono">{asset.serial_number || '-'}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">Teslim Tarihi:</span>
                                <span className="ml-2">{asset.assigned_date ? new Date(asset.assigned_date).toLocaleDateString('tr-TR') : '-'}</span>
                              </div>
                              {asset.description && (
                                <div className="col-span-2">
                                  <span className="text-gray-500">Açıklama:</span>
                                  <span className="ml-2">{asset.description}</span>
                                </div>
                              )}
                            </div>
                            
                            {/* Belgeler */}
                            {asset.belgeler && asset.belgeler.length > 0 && (
                              <div className="border-t pt-3">
                                <span className="text-sm text-gray-500 block mb-2">Ekli Belgeler:</span>
                                <div className="flex flex-wrap gap-2">
                                  {asset.belgeler.map((belge, bIdx) => (
                                    <a 
                                      key={bIdx}
                                      href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded"
                                    >
                                      <FileText className="h-4 w-4" />
                                      <Download className="h-3 w-3" />
                                      {belge.dosya_adi || 'Belge'}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Kabul/Red Butonları - Sadece pending durumunda */}
                            {asset.status === 'pending' && (
                              <div className="flex gap-2 pt-3 border-t">
                                <Button
                                  onClick={() => handleAcceptAsset(asset.id)}
                                  disabled={processingAsset === asset.id}
                                  className="bg-green-600 hover:bg-green-700 text-white flex-1"
                                >
                                  {processingAsset === asset.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                  ) : (
                                    <Check className="h-4 w-4 mr-2" />
                                  )}
                                  Kabul Et
                                </Button>
                                <Button
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedAsset(asset);
                                    setShowRejectModal(true);
                                  }}
                                  disabled={processingAsset === asset.id}
                                  className="text-red-600 border-red-300 hover:bg-red-50 flex-1"
                                >
                                  <X className="h-4 w-4 mr-2" />
                                  Reddet
                                </Button>
                              </div>
                            )}
                            
                            {/* Kabul edilmiş zimmetler için "Teslim Aldı" göster (iade yalnızca depocu geri alır) */}
                            {asset.status === 'accepted' && (
                              <div className="pt-3 border-t">
                                <div className="flex items-center justify-center gap-2 bg-green-50 text-green-700 py-3 rounded-lg">
                                  <Check className="h-5 w-5" />
                                  <span className="font-semibold">Teslim Aldı</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Zimmetli demirbaş bulunmuyor</p>
                      </div>
                    )
                    )}

                    {zimmetTab === 'arsiv' && (
                      zimmetArchive.length > 0 ? (
                        <div className="space-y-4">
                          {zimmetArchive.map((item, idx) => (
                            <div key={item.id || idx} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-semibold text-lg">{item.urun_adi || '-'}</h4>
                                  <p className="text-sm text-gray-600">
                                    {item.marka && `${item.marka} `}
                                    {item.model && `${item.model} `}
                                  </p>
                                </div>
                                {item.islem === 'reddedildi' ? (
                                  <Badge className="bg-red-100 text-red-700">Reddedildi</Badge>
                                ) : item.islem === 'geri_alindi' ? (
                                  <Badge className="bg-purple-100 text-purple-700">Depo Geri Aldı</Badge>
                                ) : (
                                  <Badge className="bg-orange-100 text-orange-700">İade Edildi</Badge>
                                )}
                              </div>

                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div>
                                  <span className="text-gray-500">Seri No:</span>
                                  <span className="ml-2 font-mono">{item.seri_no || '-'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Zimmet Türü:</span>
                                  <span className="ml-2">{item.zimmet_durumu === 'zimmette' ? 'Kalıcı Zimmet' : 'Kullanıcıda (Geçici)'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Teslim Tarihi:</span>
                                  <span className="ml-2">{item.zimmet_tarihi ? new Date(item.zimmet_tarihi).toLocaleDateString('tr-TR') : '-'}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">{item.islem === 'reddedildi' ? 'Red Tarihi' : item.islem === 'geri_alindi' ? 'Geri Alma Tarihi' : 'İade Tarihi'}:</span>
                                  <span className="ml-2">{item.iade_tarihi ? new Date(item.iade_tarihi).toLocaleDateString('tr-TR') : '-'}</span>
                                </div>
                                {item.assigned_by_name && (
                                  <div>
                                    <span className="text-gray-500">Teslim Eden (Depo):</span>
                                    <span className="ml-2">{item.assigned_by_name}</span>
                                  </div>
                                )}
                                {item.islem === 'geri_alindi' && item.geri_alan_adi && (
                                  <div>
                                    <span className="text-gray-500">Geri Alan (Depo):</span>
                                    <span className="ml-2">{item.geri_alan_adi}</span>
                                  </div>
                                )}
                                {item.aciklama && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500">Teslim Açıklaması:</span>
                                    <span className="ml-2">{item.aciklama}</span>
                                  </div>
                                )}
                                {item.islem === 'reddedildi' && item.red_sebebi && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500">Red Sebebi:</span>
                                    <span className="ml-2 text-red-600">{item.red_sebebi}</span>
                                  </div>
                                )}
                                {item.iade_notu && (
                                  <div className="col-span-2">
                                    <span className="text-gray-500">İade Notu:</span>
                                    <span className="ml-2">{item.iade_notu}</span>
                                  </div>
                                )}
                              </div>

                              {/* Teslim sırasında depocunun eklediği belgeler */}
                              {item.belgeler && item.belgeler.length > 0 && (
                                <div className="border-t pt-3">
                                  <span className="text-sm text-gray-500 block mb-2">Teslim Belgeleri:</span>
                                  <div className="flex flex-wrap gap-2">
                                    {item.belgeler.map((belge, bIdx) => (
                                      <a
                                        key={bIdx}
                                        href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded"
                                      >
                                        <FileText className="h-4 w-4" />
                                        <Download className="h-3 w-3" />
                                        {belge.dosya_adi || 'Belge'}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {/* Geri alma (çıkış) sırasında eklenen belgeler */}
                              {item.geri_alma_belgeler && item.geri_alma_belgeler.length > 0 && (
                                <div className="border-t pt-3">
                                  <span className="text-sm text-gray-500 block mb-2">Geri Alma (Çıkış) Belgeleri:</span>
                                  <div className="flex flex-wrap gap-2">
                                    {item.geri_alma_belgeler.map((belge, bIdx) => (
                                      <a
                                        key={bIdx}
                                        href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-800 bg-orange-50 px-2 py-1 rounded"
                                      >
                                        <FileText className="h-4 w-4" />
                                        <Download className="h-3 w-3" />
                                        {belge.dosya_adi || 'Belge'}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Arşivde zimmet bulunmuyor</p>
                        </div>
                      )
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Depodan Alınanlar */}
              {activeSection === 'depodan' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Warehouse className="h-5 w-5" />
                      Depodan Alınanlar
                      <Badge variant="outline" className="ml-2">{depodanAlinanlar.total_count} ürün</Badge>
                      {depodanAlinanlar.pending_count > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-700 ml-2">
                          {depodanAlinanlar.pending_count} onay bekliyor
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Alt sekmeler: Güncel / Arşiv */}
                    <div className="flex gap-2 mb-4 border-b">
                      <button
                        type="button"
                        onClick={() => setDepoTab('guncel')}
                        data-testid="depo-tab-guncel"
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${depoTab === 'guncel' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      >
                        Güncel
                      </button>
                      <button
                        type="button"
                        onClick={() => setDepoTab('arsiv')}
                        data-testid="depo-tab-arsiv"
                        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${depoTab === 'arsiv' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                      >
                        Arşiv ({depoZimmetArchive.length})
                      </button>
                    </div>

                    {depoTab === 'guncel' && (
                    ((depodanAlinanlar.items && depodanAlinanlar.items.length > 0) || temporaryAssets.length > 0) ? (
                      <div className="space-y-4">
                        {/* Depodan alınan demirbaş/zimmet (Kullanıcıda - geçici) */}
                        {temporaryAssets.length > 0 && (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-blue-700 flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              Aldığım Demirbaş/Zimmetler (Kullanıcıda)
                            </h4>
                            {temporaryAssets.map((asset, idx) => (
                              <div key={asset.id || idx} className="border rounded-lg p-4 space-y-3 bg-white">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <h4 className="font-semibold text-lg">{asset.name || asset.asset_name}</h4>
                                    <p className="text-sm text-gray-600">
                                      {asset.brand && `${asset.brand} `}{asset.model && `${asset.model} `}
                                      {asset.serial_number && `- ${asset.serial_number}`}
                                    </p>
                                  </div>
                                  {asset.status === 'pending' ? (
                                    <Badge className="bg-yellow-100 text-yellow-700">Onay Bekliyor</Badge>
                                  ) : (
                                    <Badge className="bg-green-100 text-green-700">Kabul Edildi</Badge>
                                  )}
                                </div>
                                {asset.description && (
                                  <p className="text-sm text-gray-500">Açıklama: {asset.description}</p>
                                )}
                                {asset.belgeler && asset.belgeler.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {asset.belgeler.map((belge, bIdx) => (
                                      <a key={bIdx} href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">
                                        <FileText className="h-4 w-4" /><Download className="h-3 w-3" />{belge.dosya_adi || 'Belge'}
                                      </a>
                                    ))}
                                  </div>
                                )}
                                {asset.status === 'pending' ? (
                                  <div className="flex gap-2 pt-2 border-t">
                                    <Button onClick={() => handleAcceptAsset(asset.id)} disabled={processingAsset === asset.id}
                                      className="flex-1 bg-green-600 hover:bg-green-700" data-testid={`depo-accept-asset-${asset.id}`}>
                                      {processingAsset === asset.id ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                                      Teslim Al
                                    </Button>
                                    <Button variant="outline" onClick={() => { setSelectedAsset(asset); setShowRejectModal(true); }}
                                      disabled={processingAsset === asset.id} className="flex-1 text-red-600 border-red-300 hover:bg-red-50"
                                      data-testid={`depo-reject-asset-${asset.id}`}>
                                      <X className="h-4 w-4 mr-2" />Reddet
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="pt-2 border-t">
                                    <div className="flex items-center justify-center gap-2 bg-green-50 text-green-700 py-3 rounded-lg">
                                      <Check className="h-5 w-5" /><span className="font-semibold">Teslim Aldı</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {depodanAlinanlar.items && depodanAlinanlar.items.length > 0 && (
                        <>
                        {/* Onay Bekleyen Ürünler */}
                        {depodanAlinanlar.items.filter(item => item.approval_status === 'pending').length > 0 && (
                          <div className="border-2 border-yellow-200 rounded-lg p-4 bg-yellow-50">
                            <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              Onay Bekleyen Ürünler
                            </h4>
                            <div className="space-y-3">
                              {depodanAlinanlar.items
                                .filter(item => item.approval_status === 'pending')
                                .reduce((acc, item) => {
                                  // Aynı cikis_id'ye sahip ürünleri grupla
                                  const existing = acc.find(x => x.id === item.id);
                                  if (existing) {
                                    existing.items.push(item);
                                  } else {
                                    acc.push({
                                      id: item.id,
                                      date: item.date,
                                      taseron_firma: item.taseron_firma,
                                      created_by_name: item.created_by_name,
                                      items: [item]
                                    });
                                  }
                                  return acc;
                                }, [])
                                .map((group) => (
                                  <div key={group.id} className="bg-white rounded-lg p-4 border border-yellow-300">
                                    <div className="flex justify-between items-start mb-3">
                                      <div>
                                        <p className="text-sm text-gray-600">
                                          {group.date ? new Date(group.date).toLocaleDateString('tr-TR') : '-'}
                                          {group.taseron_firma && ` • ${group.taseron_firma}`}
                                        </p>
                                        <p className="text-xs text-gray-500">Veren: {group.created_by_name || '-'}</p>
                                      </div>
                                      <Badge className="bg-yellow-100 text-yellow-700">Onay Bekliyor</Badge>
                                    </div>
                                    
                                    {/* Ürünler Listesi */}
                                    <div className="space-y-2 mb-4">
                                      {group.items.map((item, idx) => (
                                        <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                          <span className="font-medium">{item.product_name}</span>
                                          <div className="flex items-center gap-2">
                                            <Badge className="bg-blue-100 text-blue-700">{item.quantity}</Badge>
                                            <span className="text-sm text-gray-500">{item.unit || 'Adet'}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    
                                    {/* Onay/Red Butonları */}
                                    <div className="flex gap-2 pt-3 border-t border-gray-200">
                                      <Button
                                        onClick={() => handleApproveDepoItem(group.id)}
                                        disabled={processingDepoItem === group.id}
                                        className="bg-green-600 hover:bg-green-700 text-white flex-1"
                                        data-testid={`approve-depo-${group.id}`}
                                      >
                                        {processingDepoItem === group.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                          <Check className="h-4 w-4 mr-2" />
                                        )}
                                        Onayla
                                      </Button>
                                      <Button
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedDepoItem(group);
                                          setShowDepoRejectModal(true);
                                        }}
                                        disabled={processingDepoItem === group.id}
                                        className="text-red-600 border-red-300 hover:bg-red-50 flex-1"
                                        data-testid={`reject-depo-${group.id}`}
                                      >
                                        <X className="h-4 w-4 mr-2" />
                                        Reddet
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Onaylanmış Ürünler */}
                        {depodanAlinanlar.items.filter(item => item.approval_status === 'approved').length > 0 && (
                          <div>
                            <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              Onaylanmış Ürünler
                            </h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ürün Adı</TableHead>
                                  <TableHead className="text-center">Miktar</TableHead>
                                  <TableHead>Birim</TableHead>
                                  <TableHead>Tarih</TableHead>
                                  <TableHead>Taşeron/Firma</TableHead>
                                  <TableHead>Durum</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {depodanAlinanlar.items
                                  .filter(item => item.approval_status === 'approved')
                                  .map((item, idx) => (
                                    <TableRow key={`approved-${item.id}-${idx}`}>
                                      <TableCell className="font-medium">{item.product_name}</TableCell>
                                      <TableCell className="text-center">
                                        <Badge className="bg-blue-100 text-blue-700">{item.quantity}</Badge>
                                      </TableCell>
                                      <TableCell>{item.unit || 'Adet'}</TableCell>
                                      <TableCell>{item.date ? new Date(item.date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                                      <TableCell>{item.taseron_firma || '-'}</TableCell>
                                      <TableCell>
                                        <Badge className="bg-green-100 text-green-700">
                                          <Check className="h-3 w-3 mr-1" />
                                          Onaylandı
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}

                        {/* Reddedilmiş Ürünler */}
                        {depodanAlinanlar.items.filter(item => item.approval_status === 'rejected').length > 0 && (
                          <div>
                            <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                              <X className="h-4 w-4" />
                              Reddedilmiş Ürünler
                            </h4>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ürün Adı</TableHead>
                                  <TableCell className="text-center">Miktar</TableCell>
                                  <TableHead>Tarih</TableHead>
                                  <TableHead>Durum</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {depodanAlinanlar.items
                                  .filter(item => item.approval_status === 'rejected')
                                  .map((item, idx) => (
                                    <TableRow key={`rejected-${item.id}-${idx}`} className="bg-red-50">
                                      <TableCell className="font-medium">{item.product_name}</TableCell>
                                      <TableCell className="text-center">
                                        <Badge className="bg-gray-100 text-gray-600">{item.quantity}</Badge>
                                      </TableCell>
                                      <TableCell>{item.date ? new Date(item.date).toLocaleDateString('tr-TR') : '-'}</TableCell>
                                      <TableCell>
                                        <Badge className="bg-red-100 text-red-700">
                                          <X className="h-3 w-3 mr-1" />
                                          Reddedildi
                                        </Badge>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                        
                        {/* Bilgi Notu */}
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-sm text-blue-700">
                            <strong>Bilgi:</strong> Depo tarafından size verilen ürünleri onayladığınızda stoktan düşülür. 
                            Reddettiğinizde ürün stokta kalır ve işlem iptal edilir.
                          </p>
                        </div>
                        </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Warehouse className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Depodan alınan ürün bulunmuyor</p>
                      </div>
                    )
                    )}

                    {depoTab === 'arsiv' && (
                      depoZimmetArchive.length > 0 ? (
                        <div className="space-y-4">
                          {depoZimmetArchive.map((item, idx) => (
                            <div key={item.id || idx} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-semibold text-lg">{item.urun_adi || '-'}</h4>
                                  <p className="text-sm text-gray-600">
                                    {item.marka && `${item.marka} `}{item.model && `${item.model} `}
                                  </p>
                                </div>
                                {item.islem === 'reddedildi' ? (
                                  <Badge className="bg-red-100 text-red-700">Reddedildi</Badge>
                                ) : item.islem === 'geri_alindi' ? (
                                  <Badge className="bg-purple-100 text-purple-700">Depo Geri Aldı</Badge>
                                ) : (
                                  <Badge className="bg-orange-100 text-orange-700">İade Edildi</Badge>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                <div><span className="text-gray-500">Seri No:</span><span className="ml-2 font-mono">{item.seri_no || '-'}</span></div>
                                <div><span className="text-gray-500">Teslim Tarihi:</span><span className="ml-2">{item.zimmet_tarihi ? new Date(item.zimmet_tarihi).toLocaleDateString('tr-TR') : '-'}</span></div>
                                <div><span className="text-gray-500">{item.islem === 'reddedildi' ? 'Red Tarihi' : item.islem === 'geri_alindi' ? 'Geri Alma Tarihi' : 'İade Tarihi'}:</span><span className="ml-2">{item.iade_tarihi ? new Date(item.iade_tarihi).toLocaleDateString('tr-TR') : '-'}</span></div>
                                {item.assigned_by_name && (<div><span className="text-gray-500">Teslim Eden (Depo):</span><span className="ml-2">{item.assigned_by_name}</span></div>)}
                                {item.islem === 'geri_alindi' && item.geri_alan_adi && (<div><span className="text-gray-500">Geri Alan (Depo):</span><span className="ml-2">{item.geri_alan_adi}</span></div>)}
                                {item.aciklama && (<div className="col-span-2"><span className="text-gray-500">Teslim Açıklaması:</span><span className="ml-2">{item.aciklama}</span></div>)}
                                {item.islem === 'reddedildi' && item.red_sebebi && (<div className="col-span-2"><span className="text-gray-500">Red Sebebi:</span><span className="ml-2 text-red-600">{item.red_sebebi}</span></div>)}
                              </div>
                              {item.belgeler && item.belgeler.length > 0 && (
                                <div className="border-t pt-3">
                                  <span className="text-sm text-gray-500 block mb-2">Teslim Belgeleri:</span>
                                  <div className="flex flex-wrap gap-2">
                                    {item.belgeler.map((belge, bIdx) => (
                                      <a key={bIdx} href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 bg-blue-50 px-2 py-1 rounded">
                                        <FileText className="h-4 w-4" /><Download className="h-3 w-3" />{belge.dosya_adi || 'Belge'}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {item.geri_alma_belgeler && item.geri_alma_belgeler.length > 0 && (
                                <div className="border-t pt-3">
                                  <span className="text-sm text-gray-500 block mb-2">Geri Alma (Çıkış) Belgeleri:</span>
                                  <div className="flex flex-wrap gap-2">
                                    {item.geri_alma_belgeler.map((belge, bIdx) => (
                                      <a key={bIdx} href={belge.url && (belge.url.startsWith('http') || belge.url.startsWith('data:')) ? belge.url : `${BACKEND_URL}${belge.url}`} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-800 bg-orange-50 px-2 py-1 rounded">
                                        <FileText className="h-4 w-4" /><Download className="h-3 w-3" />{belge.dosya_adi || 'Belge'}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <Warehouse className="h-12 w-12 mx-auto mb-2 opacity-50" />
                          <p>Arşivde kayıt bulunmuyor</p>
                        </div>
                      )
                    )}
                  </CardContent>
                </Card>
              )}

              {/* İK İşler */}
              {activeSection === 'ikisler' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      İK İşler
                    </CardTitle>
                    <CardDescription>İnsan Kaynakları ile yapılan belge yazışmalarınız (tutanak, savunma vb.) konuya göre arşivlenir.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {hrWorkTitles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>Henüz İK belge yazışmanız bulunmuyor</p>
                      </div>
                    ) : (
                      <div>
                        {/* Belge adına göre alt sekmeler */}
                        <div className="flex gap-2 mb-4 border-b flex-wrap">
                          {hrWorkTitles.map(title => (
                            <button
                              key={title}
                              type="button"
                              onClick={() => setHrWorkTab(title)}
                              data-testid={`hrwork-tab-${title}`}
                              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${hrWorkTab === title ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                            >
                              {title} ({hrWorks.filter(d => (d.title || 'Diğer').trim() === title).length})
                            </button>
                          ))}
                        </div>

                        {/* Seçili belge adına ait yazışmalar */}
                        <div className="space-y-4">
                          {hrWorkEntries.map((entry, idx) => (
                            <div key={entry.id || idx} className="border rounded-lg overflow-hidden">
                              {/* İK'dan gelen */}
                              <div className="p-4 bg-blue-50 border-b border-blue-100">
                                <div className="flex justify-between items-start mb-2">
                                  <div className="flex items-center gap-2">
                                    <Badge className="bg-blue-600 text-white">İK'dan Gelen</Badge>
                                    <span className="font-semibold">{entry.title}</span>
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {entry.created_at ? new Date(entry.created_at).toLocaleString('tr-TR') : '-'}
                                  </span>
                                </div>
                                {entry.description && (
                                  <p className="text-sm text-gray-700 mb-2">{entry.description}</p>
                                )}
                                <p className="text-xs text-gray-500 mb-2">Gönderen: {entry.created_by_name || 'İK'}</p>
                                {entry.documents && entry.documents.length > 0 && (
                                  <div className="flex flex-wrap gap-2">
                                    {entry.documents.map((doc, dIdx) => (
                                      <Button key={dIdx} variant="outline" size="sm" className="text-xs"
                                        onClick={() => { const link = document.createElement('a'); link.href = doc.data; link.download = doc.name; link.click(); }}
                                        data-testid={`hrwork-incoming-doc-${idx}-${dIdx}`}>
                                        <Download className="h-3 w-3 mr-1" />{doc.name}
                                      </Button>
                                    ))}
                                  </div>
                                )}
                              </div>
                              {/* Çalışanın cevabı */}
                              <div className="p-4 bg-white">
                                {entry.my_response ? (
                                  <>
                                    <div className="flex justify-between items-start mb-2">
                                      <Badge className="bg-green-100 text-green-700">Cevabınız</Badge>
                                      <span className="text-xs text-gray-500">
                                        {entry.my_response.responded_at ? new Date(entry.my_response.responded_at).toLocaleString('tr-TR') : '-'}
                                      </span>
                                    </div>
                                    {entry.my_response.message && (
                                      <p className="text-sm text-gray-700 mb-2">{entry.my_response.message}</p>
                                    )}
                                    {entry.my_response.documents && entry.my_response.documents.length > 0 && (
                                      <div className="flex flex-wrap gap-2">
                                        {entry.my_response.documents.map((doc, dIdx) => (
                                          <Button key={dIdx} variant="outline" size="sm" className="text-xs"
                                            onClick={() => { const link = document.createElement('a'); link.href = doc.data; link.download = doc.name; link.click(); }}
                                            data-testid={`hrwork-reply-doc-${idx}-${dIdx}`}>
                                            <Download className="h-3 w-3 mr-1" />{doc.name}
                                          </Button>
                                        ))}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <div className="flex items-center gap-2 text-sm text-yellow-700">
                                    <Clock className="h-4 w-4" />
                                    Henüz cevaplanmadı — "İK Talepleri" sekmesinden cevaplayabilirsiniz.
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}


              {/* Zimmet Red Modalı */}
              <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600">Zimmet Reddet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {selectedAsset && (
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="font-medium">{selectedAsset.name || selectedAsset.asset_name}</p>
                        <p className="text-sm text-gray-600">
                          {selectedAsset.brand} {selectedAsset.model} - {selectedAsset.serial_number}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-2">Red Sebebi *</label>
                      <Textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Neden reddettiğinizi açıklayın..."
                        rows={3}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => {
                      setShowRejectModal(false);
                      setSelectedAsset(null);
                      setRejectReason('');
                    }}>
                      İptal
                    </Button>
                    <Button 
                      onClick={handleRejectAsset}
                      disabled={!rejectReason.trim() || processingAsset}
                      className="bg-red-600 hover:bg-red-700 text-white"
                    >
                      {processingAsset ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Reddet
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Depo Stok Çıkışı Red Modalı */}
              <Dialog open={showDepoRejectModal} onOpenChange={setShowDepoRejectModal}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="text-red-600">Teslim Almayı Reddet</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {selectedDepoItem && (
                      <div className="bg-gray-50 p-3 rounded">
                        <p className="text-sm text-gray-600 mb-2">
                          Tarih: {selectedDepoItem.date ? new Date(selectedDepoItem.date).toLocaleDateString('tr-TR') : '-'}
                        </p>
                        <div className="space-y-1">
                          {selectedDepoItem.items && selectedDepoItem.items.map((item, idx) => (
                            <p key={idx} className="font-medium">
                              {item.product_name} - {item.quantity} {item.unit || 'Adet'}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium mb-2">Red Sebebi *</label>
                      <Textarea
                        value={depoRejectReason}
                        onChange={(e) => setDepoRejectReason(e.target.value)}
                        placeholder="Neden reddettiğinizi açıklayın..."
                        rows={3}
                        data-testid="depo-reject-reason"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => {
                      setShowDepoRejectModal(false);
                      setSelectedDepoItem(null);
                      setDepoRejectReason('');
                    }}>
                      İptal
                    </Button>
                    <Button 
                      onClick={handleRejectDepoItem}
                      disabled={!depoRejectReason.trim() || processingDepoItem}
                      className="bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-depo-reject"
                    >
                      {processingDepoItem ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Reddet
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyInfo;
